import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PaperBall } from "@/components/trash/CrumpledPaper";

export const DRAG_TYPE = "application/x-lead-complete";
export const TRASH_CAN_ATTR = "data-trash-can";

interface TrashCanContextValue {
  flyToTrash: (sourceRect: DOMRect) => void;
  isDragOver: boolean;
  setIsDragOver: (v: boolean) => void;
  drawerOpen: boolean;
  setDrawerOpen: (v: boolean) => void;
  isBouncing: boolean;
  triggerBounce: () => void;
}

const TrashCanContext = createContext<TrashCanContextValue | null>(null);

export function useTrashCan() {
  const ctx = useContext(TrashCanContext);
  if (!ctx) throw new Error("useTrashCan must be used within TrashCanProvider");
  return ctx;
}

interface FlyState {
  key: number;
  sourceX: number;
  sourceY: number;
  destX: number;
  destY: number;
}

function getVisibleTrashCanRect(): DOMRect {
  const candidates = document.querySelectorAll<HTMLElement>(`[${TRASH_CAN_ATTR}]`);
  for (const el of candidates) {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const style = window.getComputedStyle(el);
      if (style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0") {
        return rect;
      }
    }
  }
  return new DOMRect(window.innerWidth - 40, 40, 40, 40);
}

export function TrashCanProvider({ children }: { children: ReactNode }) {
  const [flyState, setFlyState] = useState<FlyState | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isBouncing, setIsBouncing] = useState(false);

  const triggerBounce = useCallback(() => {
    setIsBouncing(true);
    setTimeout(() => setIsBouncing(false), 500);
  }, []);

  const flyToTrash = useCallback((sourceRect: DOMRect) => {
    const trashRect = getVisibleTrashCanRect();
    setFlyState((prev) => ({
      key: (prev?.key ?? 0) + 1,
      sourceX: sourceRect.left + sourceRect.width / 2,
      sourceY: sourceRect.top + sourceRect.height / 2,
      destX: trashRect.left + trashRect.width / 2,
      destY: trashRect.top + trashRect.height * 0.35,
    }));
  }, []);

  return (
    <TrashCanContext.Provider
      value={{
        flyToTrash,
        isDragOver,
        setIsDragOver,
        drawerOpen,
        setDrawerOpen,
        isBouncing,
        triggerBounce,
      }}
    >
      {children}
      <AnimatePresence>
        {flyState && (
          <CrumpleFlyAnimation
            key={flyState.key}
            sourceX={flyState.sourceX}
            sourceY={flyState.sourceY}
            destX={flyState.destX}
            destY={flyState.destY}
            onComplete={() => {
              setFlyState(null);
              triggerBounce();
            }}
          />
        )}
      </AnimatePresence>
    </TrashCanContext.Provider>
  );
}

function CrumpleFlyAnimation({
  sourceX,
  sourceY,
  destX,
  destY,
  onComplete,
}: {
  sourceX: number;
  sourceY: number;
  destX: number;
  destY: number;
  onComplete: () => void;
}) {
  const SIZE = 52;
  const half = SIZE / 2;
  const midX = sourceX + (destX - sourceX) * 0.38;
  const midY = Math.min(sourceY, destY) - 160;

  return (
    <motion.div
      className="fixed z-[9999] pointer-events-none"
      style={{ top: 0, left: 0 }}
      initial={{
        x: sourceX - half,
        y: sourceY - half,
        scale: 1,
        rotate: 0,
        opacity: 1,
      }}
      animate={{
        x: [sourceX - half, midX - half, destX - half],
        y: [sourceY - half, midY - half, destY - half],
        scale: [1, 0.7, 0.22],
        rotate: [0, -80, -300],
        opacity: [1, 1, 0],
      }}
      transition={{
        duration: 0.7,
        ease: [0.22, 0.1, 0.82, 0.92],
        times: [0, 0.5, 1],
      }}
      onAnimationComplete={onComplete}
    >
      <CrumplingSheet size={SIZE} />
    </motion.div>
  );
}

function CrumplingSheet({ size }: { size: number }) {
  const FLAT_SHAPE = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
  const CRUMPLED_SHAPE =
    "polygon(52% 2%, 78% 6%, 96% 22%, 100% 48%, 88% 74%, 70% 95%, 42% 100%, 14% 92%, 2% 68%, 0% 38%, 12% 14%, 32% 2%)";

  return (
    <motion.div
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle at 33% 30%, #fefbf0 0%, #f0e2bc 55%, #d9c49a 100%)`,
        boxShadow: `1px 2px 5px rgba(0,0,0,0.28)`,
        position: "relative",
        overflow: "hidden",
      }}
      animate={{
        clipPath: [FLAT_SHAPE, CRUMPLED_SHAPE, CRUMPLED_SHAPE],
        borderRadius: ["4px", "20%", "50%"],
      }}
      transition={{
        clipPath: {
          duration: 0.7,
          ease: "easeIn",
          times: [0, 0.45, 1],
        },
        borderRadius: {
          duration: 0.7,
          ease: "easeIn",
          times: [0, 0.45, 1],
        },
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          inset: 0,
        }}
        animate={{
          background: [
            "repeating-linear-gradient(0deg, transparent, transparent 8px, rgba(120,90,30,0.04) 8px, rgba(120,90,30,0.04) 9px)",
            "repeating-linear-gradient(-52deg, transparent, transparent 4px, rgba(120,90,30,0.10) 4px, rgba(120,90,30,0.10) 5px)",
          ],
        }}
        transition={{ duration: 0.35, delay: 0.1 }}
      />
    </motion.div>
  );
}

export { PaperBall };
